import React, { useEffect, useState } from 'react';
import apiDefinitions from '../../../../core/api/apiDefinitions';

interface FuelType {
  id: number;
  fuelTypeName: string;
}

interface StepOneProps {
  vehicleData: any;
  setVehicleData: React.Dispatch<React.SetStateAction<any>>;
  vehicleTypes: any[];
  features: any[];
  config: any;
  isLoading: any;
  brandData: any;
}
const todayStr = new Date().toISOString().split('T')[0];
const clampToToday = (v: string) => (v && v > todayStr ? todayStr : v || null);
const StepOne: React.FC<StepOneProps> = ({
  vehicleData,
  setVehicleData,
  vehicleTypes,
  features,
  config,
  isLoading,
  brandData,
}) => {
  const [selectedBrandId, setSelectedBrandId] = useState('');
  const [fuelTypes, setFuelTypes] = useState<FuelType[]>([]);
  const [kmErrors, setKmErrors] = useState<{
    lastGeneralServiceKms?: string;
    lastAdvancedServiceKms?: string;
    lastTyreChangeKms?: string;
  }>({});

  // Small helpers
  const toInt = (v: any) => {
    const n = parseInt(v as any, 10);
    return Number.isFinite(n) ? n : 0;
  };
  const getNoOfKms = () => toInt(vehicleData?.noOfKms);

  // Enforce ≤ Number of Kilometers with clamping + error
  const setKmWithClamp = (
    field: 'lastGeneralServiceKms' | 'lastAdvancedServiceKms' | 'lastTyreChangeKms'|'generalServiceStandardKms',
    raw: any
  ) => {
    const noOfKms = getNoOfKms();

    // Special handling for "required" on lastGeneralServiceKms
    if (field === 'lastGeneralServiceKms' && (raw === '' || raw === null)) {
      setKmErrors(prev => ({ ...prev, [field]: 'Required' }));
      setVehicleData((prev: any) => ({ ...prev, [field]: 0 }));
      return;
    }

    let value = toInt(raw);

    if (noOfKms > 0 && value > noOfKms) {
      value = noOfKms; // clamp
      setKmErrors(prev => ({ ...prev, [field]: 'Must be ≤ Number of Kilometers' }));
    } else {
      // clear error (unless still empty and required—handled above)
      setKmErrors(prev => ({ ...prev, [field]: '' }));
    }

    setVehicleData((prev: any) => ({ ...prev, [field]: value }));
  };

  const handleLastGeneralChange = (raw: string) => {
    setKmWithClamp('lastGeneralServiceKms', raw);
  };
  const handleStandardGeneralChange = (raw: string) => {
    setKmWithClamp('generalServiceStandardKms', raw);
  };
const currentYear = new Date().getFullYear();
const clampYear = (y: number) => {
  if (!Number.isFinite(y)) return 0;
  if (y > currentYear) return currentYear;
  if (y < 1900) return 1900;
  return y;
};

// keep a string for the input so partial typing doesn't jump
const [yearInput, setYearInput] = useState(
  vehicleData.manufacturingYear?.toString() ?? ''
);

  // If Number of Kilometers decreases, auto-clamp existing values
  useEffect(() => {
    const noOfKms = getNoOfKms();
    if (!noOfKms || noOfKms <= 0) return;

    const updates: any = {};
    const newErrors: any = { ...kmErrors };
    (['lastGeneralServiceKms', 'lastAdvancedServiceKms', 'lastTyreChangeKms','generalServiceStandardKms'] as const).forEach(field => {
      const current = vehicleData[field] === '' ? '' : toInt(vehicleData[field]);
      if (current !== '' && current > noOfKms) {
        updates[field] = noOfKms;
        newErrors[field] = 'Clamped to Number of Kilometers';
      }
    });

    if (Object.keys(updates).length) {
      setVehicleData((prev: any) => ({ ...prev, ...updates }));
      setKmErrors(newErrors);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleData.noOfKms]);

  // Fetch fuel types on mount
  useEffect(() => {
    const fetchFuelTypes = async () => {
      try {
        const response = await apiDefinitions.getAllVehicleFuelTypes();
        if (response.data.status === 'S1000' && Array.isArray(response.data.data)) {
          setFuelTypes(
            response.data.data.map((f: any) => ({
              id: f.id,
              fuelTypeName: f.fuelTypeName,
            }))
          );
        } else {
          // handle non-success if needed
        }
      } catch (error) {
        // handle error if needed
      }
    };
    fetchFuelTypes();
  }, []);

  useEffect(() => {
    if (vehicleData.brand && brandData?.data?.vehicleBrands) {
      const selectedBrand = brandData.data.vehicleBrands.find((b: any) => b.name === vehicleData.brand);
      setSelectedBrandId(selectedBrand?.id?.toString() || '');
    }
  }, [vehicleData.brand, brandData]);

  const handleBrandChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const brandId = e.target.value;
    setSelectedBrandId(brandId);
    const selectedBrand = brandData?.data.vehicleBrands.find((b: any) => b.id === parseInt(brandId));
    setVehicleData({
      ...vehicleData,
      brand: selectedBrand?.name || '',
      model: '',
      brandId: parseInt(brandId) || 0,
      modelId: 0,
    });
  };

  const handleModelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedModelName = e.target.value;
    const selectedBrand = brandData?.data.vehicleBrands.find((b: any) => b.id === vehicleData.brandId);
    const selectedModel = selectedBrand?.models?.find((m: any) => m.name === selectedModelName);
    setVehicleData({
      ...vehicleData,
      model: selectedModelName,
      modelId: selectedModel?.id || 0,
    });
  };

  // Normalize date input to YYYY-MM-DD for display
  const normalizeDate = (date: string): string => {
    if (!date) return '';
    const d = new Date(date);
    return isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
  };

  const isBike = vehicleData.vehicleTypeId.type === 'Motorcycle' || vehicleData.vehicleTypeId.type === 'Bike';
  const isElectric = (vehicleData.fuelType || '').toLowerCase() === 'electric';

  // Dynamic max attribute (prevents typing above noOfKms at browser level too)
  const maxToNoOfKms = () => {
    const v = getNoOfKms();
    return v > 0 ? v : undefined;
  };

  return (
    <div className="row">
      <div className="col-xl-12">
        <div className="card">
          <div className="card-header">
            <div className="card-title">Vehicle Details</div>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-6">
                {/* Number Plate */}
                <div className="mb-3">
                  <label className="form-label">Number Plate <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    value={vehicleData.numberPlate}
                    onChange={(e) =>
                      setVehicleData({ ...vehicleData, numberPlate: e.target.value.toUpperCase() })
                    }
                    placeholder="Enter number plate"
                  />
                </div>

                {/* Vehicle Type */}
                <div className="mb-3">
                  <label className="form-label">Vehicle Type <span className="text-danger">*</span></label>
                  <select
                    className="form-select"
                    value={vehicleData.vehicleTypeId.id || ''}
                    onChange={(e) => {
                      const selectedType = vehicleTypes.find((vt) => vt.id === parseInt(e.target.value));
                      setVehicleData({
                        ...vehicleData,
                        vehicleTypeId: { id: selectedType?.id || 0, type: selectedType?.type || '' },
                        frontTyreSize: '',
                        rearTyreSize: '',
                        tyreSize: '',
                      });
                    }}
                    disabled={isLoading.vehicleTypes}
                  >
                    <option value="">Select Vehicle Type</option>
                    {vehicleTypes.map((vt) => (
                      <option key={vt.id} value={vt.id}>
                        {vt.type}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Brand */}
                <div className="mb-3">
                  <label className="form-label">Brand <span className="text-danger">*</span></label>
                  <select
                    className="form-select"
                    value={selectedBrandId}
                    onChange={handleBrandChange}
                    disabled={isLoading.brands}
                  >
                    <option value="">Select Brand</option>
                    {brandData?.data.vehicleBrands?.map((brand: any) => (
                      <option key={brand.id} value={brand.id}>
                        {brand.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Model */}
                <div className="mb-3">
                  <label className="form-label">Model <span className="text-danger">*</span></label>
                  <select
                    className="form-select"
                    value={vehicleData.model}
                    onChange={handleModelChange}
                    disabled={isLoading.brands || !selectedBrandId}
                  >
                    <option value="">Select Model</option>
                    {brandData?.data.vehicleBrands
                      .find((b: any) => b.id === parseInt(selectedBrandId))
                      ?.models?.map((model: any) => (
                        <option key={model.id} value={model.name}>
                          {model.name}
                        </option>
                      ))}
                  </select>
                </div>

                {/* Vehicle Class */}
                <div className="mb-3">
                  <label className="form-label">Vehicle Class <span className="text-danger">*</span></label>
                  <select
                    className="form-select"
                    value={vehicleData.vehicleClassId || ''}
                    onChange={(e) =>
                      setVehicleData({ ...vehicleData, vehicleClassId: parseInt(e.target.value) || 0 })
                    }
                    disabled={isLoading.config}
                  >
                    <option value="">Select Vehicle Class</option>
                    {config.vehicleClasses.map((vc: string, index: number) => (
                      <option key={index + 1} value={index + 1}>
                        {vc}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Condition */}
                <div className="mb-3">
                  <label className="form-label">Condition <span className="text-danger">*</span></label>
                  <select
                    className="form-select"
                    value={vehicleData.conditionId || ''}
                    onChange={(e) =>
                      setVehicleData({
                        ...vehicleData,
                        conditionId: parseInt(e.target.value) || 0,
                        condition: config.conditions[parseInt(e.target.value) - 1] || '',
                      })
                    }
                    disabled={isLoading.config}
                  >
                    <option value="">Select Condition</option>
                    {config.conditions.map((c: string, index: number) => (
                      <option key={index + 1} value={index + 1}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Gear Type */}
                <div className="mb-3">
                  <label className="form-label">Gear Type <span className="text-danger">*</span></label>
                  <select
                    className="form-select"
                    value={vehicleData.gearTypeId || ''}
                    onChange={(e) =>
                      setVehicleData({
                        ...vehicleData,
                        gearTypeId: parseInt(e.target.value) || 0,
                        gearType: config.gearTypes[parseInt(e.target.value) - 1] || '',
                      })
                    }
                    disabled={isLoading.config}
                  >
                    <option value="">Select Gear Type</option>
                    {config.gearTypes.map((g: string, index: number) => (
                      <option key={index + 1} value={index + 1}>
                        {g}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Fuel Type */}
                <div className="mb-3">
                  <label className="form-label">Fuel Type <span className="text-danger">*</span></label>
                  <select
                    className="form-select"
                    value={vehicleData.fuelTypeId.id || ''}
                    onChange={(e) => {
                      const selectedFuelType = fuelTypes.find((ft) => ft.id === parseInt(e.target.value));
                      const fuelTypeName = selectedFuelType?.fuelTypeName || '';
                      setVehicleData({
                        ...vehicleData,
                        fuelTypeId: { id: selectedFuelType?.id || 0, fuelTypeName },
                        fuelType: fuelTypeName,
                        inStationEconomy: fuelTypeName.toLowerCase() === 'electric' ? null : vehicleData.inStationEconomy || 0,
                        outStationEconomy: fuelTypeName.toLowerCase() === 'electric' ? null : vehicleData.outStationEconomy || 0,
                        engineCapacity: fuelTypeName.toLowerCase() === 'electric' ? null : vehicleData.engineCapacity || 0,
                        batteryCapacity: fuelTypeName.toLowerCase() === 'electric' ? vehicleData.batteryCapacity || 0 : null,
                        engineNo: fuelTypeName.toLowerCase() === 'electric' ? null : vehicleData.engineNo || '',
                      });
                    }}
                    disabled={isLoading.config}
                  >
                    <option value="">Select Fuel Type</option>
                    {fuelTypes.map((ft) => (
                      <option key={ft.id} value={ft.id}>
                        {ft.fuelTypeName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Status */}
                <div className="mb-3">
                  <label className="form-label">Status <span className="text-danger">*</span></label>
                  <select
                    className="form-select"
                    value={vehicleData.statusId || ''}
                    onChange={(e) =>
                      setVehicleData({
                        ...vehicleData,
                        statusId: parseInt(e.target.value) || 0,
                        status: config.states[parseInt(e.target.value) - 1] || '',
                      })
                    }
                    disabled={isLoading.config}
                  >
                    <option value="">Select Status</option>
                    {config.states.map((s: string, index: number) => (
                      <option key={index + 1} value={index + 1}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Engine / Battery */}
                {!isElectric && (
                  <div className="mb-3">
                    <label className="form-label">Engine Capacity (cc) <span className="text-danger">*</span></label>
                    <input
                      type="number"
                      step="0.1"
                      className="form-control"
                      value={vehicleData.engineCapacity || ''}
                      onChange={(e) =>
                        setVehicleData({ ...vehicleData, engineCapacity: parseFloat(e.target.value) || 0 })
                      }
                      placeholder="Enter engine capacity"
                    />
                  </div>
                )}
                {isElectric && (
                  <div className="mb-3">
                    <label className="form-label">Battery Capacity (kWh) <span className="text-danger">*</span></label>
                    <input
                      type="number"
                      step="0.1"
                      className="form-control"
                      value={vehicleData.batteryCapacity || ''}
                      onChange={(e) =>
                        setVehicleData({ ...vehicleData, batteryCapacity: parseFloat(e.target.value) || 0 })
                      }
                      placeholder="Enter battery capacity"
                    />
                  </div>
                )}
                {!isElectric && (
                  <div className="mb-3">
                    <label className="form-label">Engine Number <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      value={vehicleData.engineNo || ''}
                      onChange={(e) => setVehicleData({ ...vehicleData, engineNo: e.target.value || '' })}
                      placeholder="Enter engine number"
                      required
                    />
                  </div>
                )}

                {/* Number of Passengers */}
                <div className="mb-3">
                  <label className="form-label">Number of Passengers <span className="text-danger">*</span></label>
                  <input
                    type="number"
                    className="form-control"
                    value={vehicleData.noOfPassengers || ''}
                    onChange={(e) =>
                      setVehicleData({ ...vehicleData, noOfPassengers: parseInt(e.target.value) || 0 })
                    }
                    placeholder="Enter number of passengers"
                  />
                </div>

                {/* Manufacturing Year */}
                <div className="mb-3">
                  <label className="form-label">Manufacturing Year <span className="text-danger">*</span></label>
                  <input
                    type="number"
                    className="form-control"
                    value={yearInput}
                    onChange={(e) => {
                      const raw = e.target.value;
                      setYearInput(raw); // don't clamp here
                      const n = Number(raw);
                      // update model only when it's a number; allow empty while typing
                      if (Number.isFinite(n)) {
                        setVehicleData({ ...vehicleData, manufacturingYear: n });
                      } else if (raw === '') {
                        setVehicleData({ ...vehicleData, manufacturingYear: undefined });
                      }
                    }}
                    onBlur={(e) => {
                      const n = Number(e.target.value);
                      if (!Number.isFinite(n)) {
                        setYearInput('');
                        return;
                      }
                      const c = clampYear(n);
                      setYearInput(String(c));
                      setVehicleData({ ...vehicleData, manufacturingYear: c });
                    }}
                    placeholder="Enter manufacturing year"
                    min={1900}           
                    max={currentYear}     
                  />
                </div>
                {/* Chassis Number */}
                <div className="mb-3">
                  <label className="form-label">Chassis Number <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    value={vehicleData.chassisNo || ''}
                    onChange={(e) => setVehicleData({ ...vehicleData, chassisNo: e.target.value || '' })}
                    placeholder="Enter chassis number"
                  />
                </div>

                {/* Owner Name */}
                <div className="mb-3">
                  <label className="form-label">Owner Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={vehicleData.ownerName || ''}
                    onChange={(e) => setVehicleData({ ...vehicleData, ownerName: e.target.value || null })}
                    placeholder="Enter owner name"
                  />
                </div>

                {/* Features */}
                <div className="card">
                  <div className="card-header">
                    <h5 className="card-title">Features</h5>
                  </div>
                  <div className="card-body">
                    {features.map((f: any) => (
                      <div className="form-check form-switch" key={f.id}>
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id={`feature-switch-${f.id}`}
                          checked={vehicleData.featureIds?.includes(f.id) || false}
                          onChange={(e) => {
                            const selected = vehicleData.featureIds || [];
                            if (e.target.checked) {
                              setVehicleData({ ...vehicleData, featureIds: [...selected, f.id] });
                            } else {
                              setVehicleData({
                                ...vehicleData,
                                featureIds: selected.filter((id: number) => id !== f.id),
                              });
                            }
                          }}
                          disabled={isLoading.features}
                        />
                        <label className="form-check-label" htmlFor={`feature-switch-${f.id}`}>
                          {f.featureName}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* RIGHT COLUMN */}
              <div className="col-md-6">
                {/* Owner Address */}
                <div className="mb-3">
                  <label className="form-label">Owner Address</label>
                  <input
                    type="text"
                    className="form-control"
                    value={vehicleData.ownerAddress || ''}
                    onChange={(e) => setVehicleData({ ...vehicleData, ownerAddress: e.target.value || null })}
                    placeholder="Enter owner address"
                  />
                </div>

                {/* Gross Weight */}
                <div className="mb-3">
                  <label className="form-label">Gross Weight (kg)</label>
                  <input
                    type="number"
                    step="0.1"
                    className="form-control"
                    value={vehicleData.grossWeight || ''}
                    onChange={(e) =>
                      setVehicleData({ ...vehicleData, grossWeight: parseFloat(e.target.value) || null })
                    }
                    placeholder="Enter gross weight"
                  />
                </div>

                {/* Vehicle Value */}
                <div className="mb-3">
                  <label className="form-label">Vehicle Value(Lkr) <span className="text-danger">*</span></label>
                  <input
                    type="number"
                    className="form-control"
                    value={vehicleData.value || ''}
                    onChange={(e) => setVehicleData({ ...vehicleData, value: parseInt(e.target.value) || 0 })}
                    placeholder="Enter vehicle value"
                  />
                </div>

                {/* Number of Kilometers */}
                <div className="mb-3">
                  <label className="form-label">Number of Kilometers <span className="text-danger">*</span></label>
                  <input
                    type="number"
                    className="form-control"
                    value={vehicleData.noOfKms || ''}
                    onChange={(e) => setVehicleData({ ...vehicleData, noOfKms: parseInt(e.target.value) || 0 })}
                    placeholder="Enter number of kilometers"
                    min={0}
                  />
                </div>

                {/* In/Out Station Economy (non-electric) */}
                {!isElectric && (
                  <>
                    <div className="mb-3">
                      <label className="form-label">In-Station Economy (km/l) <span className="text-danger">*</span></label>
                      <input
                        type="number"
                        step="0.1"
                        className="form-control"
                        value={vehicleData.inStationEconomy || ''}
                        onChange={(e) =>
                          setVehicleData({ ...vehicleData, inStationEconomy: parseFloat(e.target.value) || 0 })
                        }
                        placeholder="Enter in-station economy"
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Out-Station Economy (km/l) <span className="text-danger">*</span></label>
                      <input
                        type="number"
                        step="0.1"
                        className="form-control"
                        value={vehicleData.outStationEconomy || ''}
                        onChange={(e) =>
                          setVehicleData({ ...vehicleData, outStationEconomy: parseFloat(e.target.value) || 0 })
                        }
                        placeholder="Enter out-station economy"
                        required
                      />
                    </div>
                  </>
                )}

                {/* Rent Per Km */}
                <div className="mb-3">
                  <label className="form-label">Rent Per Kilometer(Lkr)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={vehicleData.rentPerKm || ''}
                    onChange={(e) => setVehicleData({ ...vehicleData, rentPerKm: parseInt(e.target.value) || 0 })}
                    placeholder="Enter rent per kilometer"
                  />
                </div>

                {/* Color */}
                <div className="mb-3">
                  <label className="form-label">Color <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    value={vehicleData.color || ''}
                    onChange={(e) => setVehicleData({ ...vehicleData, color: e.target.value || '' })}
                    placeholder="Enter color"
                  />
                </div>

                {/* Tyre sizes */}
                {isBike ? (
                  <>
                    <div className="mb-3">
                      <label className="form-label">Front Tyre Size  (eg:123/2-3)<span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        value={vehicleData.frontTyreSize || ''}
                        onChange={(e) => setVehicleData({ ...vehicleData, frontTyreSize: e.target.value || '' })}
                        placeholder="Enter front tyre size"
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Rear Tyre Size (eg:123/2-3) <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        value={vehicleData.rearTyreSize || ''}
                        onChange={(e) => setVehicleData({ ...vehicleData, rearTyreSize: e.target.value || '' })}
                        placeholder="Enter rear tyre size"
                      />
                    </div>
                  </>
                ) : (
                  <div className="mb-3">
                    <label className="form-label">Tyre Size (eg:123/2-3) <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      value={vehicleData.tyreSize || ''}
                      onChange={(e) => setVehicleData({ ...vehicleData, tyreSize: e.target.value || '' })}
                      placeholder="Enter tyre size"
                    />
                  </div>
                )}

                {/* General Service Standard (km) */}
                <div className="mb-1">
                  <label className="form-label">General Service Standard (km) <span className="text-danger">*</span></label>
                  <input
                    type="number"
                    className={`form-control ${kmErrors.lastGeneralServiceKms ? 'is-invalid' : ''}`}
                    value={vehicleData.generalServiceStandardKms === '' ? '' : vehicleData.generalServiceStandardKms || ''}
                    onChange={(e) => handleStandardGeneralChange(e.target.value)}
                    onBlur={(e) => handleStandardGeneralChange(e.target.value)}
                    placeholder="Enter last general service"
                    min={0}
                    max={maxToNoOfKms()}
                    required
                    title="Must be ≤ Number of Kilometers"
                  />
                  {kmErrors.lastGeneralServiceKms ? (
                    <div className="invalid-feedback">{kmErrors.lastGeneralServiceKms}</div>
                  ) : null}
                </div>
                <div className="mb-1">
                  <label className="form-label">Last General Service (km) <span className="text-danger">*</span></label>
                  <input
                    type="number"
                    className={`form-control ${kmErrors.lastGeneralServiceKms ? 'is-invalid' : ''}`}
                    value={vehicleData.lastGeneralServiceKms === '' ? '' : vehicleData.lastGeneralServiceKms || ''}
                    onChange={(e) => handleLastGeneralChange(e.target.value)}
                    onBlur={(e) => handleLastGeneralChange(e.target.value)}
                    placeholder="Enter last general service"
                    min={0}
                    max={maxToNoOfKms()}
                    required
                    title="Must be ≤ Number of Kilometers"
                  />
                  {kmErrors.lastGeneralServiceKms ? (
                    <div className="invalid-feedback">{kmErrors.lastGeneralServiceKms}</div>
                  ) : null}
                </div>

                {/* Last General Service Date */}
                <div className="mb-3">
                  <label className="form-label">Last General Service Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={normalizeDate(vehicleData.lastGeneralServiceDate)}
                    max={todayStr}                             // ✅ prevent future dates in calendar
                    onChange={(e) =>
                      setVehicleData({
                        ...vehicleData,
                        lastGeneralServiceDate: clampToToday(e.target.value), // ✅ optional clamp
                      })
                    }
                  />
                </div>

                {/* Advanced service std */}
                <div className="mb-3">
                  <label className="form-label">Advanced Service Standard (km) <span className="text-danger">*</span></label>
                  <input
                    type="number"
                    className="form-control"
                    value={vehicleData.advancedServiceStandardKms || ''}
                    onChange={(e) =>
                      setVehicleData({ ...vehicleData, advancedServiceStandardKms: parseInt(e.target.value) || 0 })
                    }
                    placeholder="Enter advanced service standard"
                    min={0}
                  />
                </div>

                {/* Last Advanced Service (km) — VALIDATED */}
                <div className="mb-1">
                  <label className="form-label">Last Advanced Service (km) <span className="text-danger">*</span></label>
                  <input
                    type="number"
                    className="form-control"
                    value={vehicleData.lastAdvancedServiceKms || ''}
                    onChange={(e) => setKmWithClamp('lastAdvancedServiceKms', e.target.value)}
                    placeholder="Enter last advanced service"
                    min={0}
                    max={maxToNoOfKms()}
                    title="Must be ≤ Number of Kilometers"
                  />
                </div>
                {kmErrors.lastAdvancedServiceKms ? (
                  <small className="text-danger">{kmErrors.lastAdvancedServiceKms}</small>
                ) : null}

                {/* Last Advanced Service Date */}
                <div className="mb-3">
                  <label className="form-label">Last Advanced Service Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={normalizeDate(vehicleData.lastAdvancedServiceDate)}
                    max={todayStr}
                    onChange={(e) =>
                      setVehicleData({
                        ...vehicleData,
                        lastAdvancedServiceDate: clampToToday(e.target.value),
                      })
                    }
                  />
                </div>

                {/* Standard Kms for Tyre Change */}
                <div className="mb-3">
                  <label className="form-label">Standard Kms for Tyre Change <span className="text-danger">*</span></label>
                  <input
                    type="number"
                    className="form-control"
                    value={vehicleData.standardKmsForTyreChange || ''}
                    onChange={(e) =>
                      setVehicleData({ ...vehicleData, standardKmsForTyreChange: parseInt(e.target.value) || 0 })
                    }
                    placeholder="Enter standard kms for tyre change"
                    min={0}
                  />
                </div>

                {/* Last Tyre Change (km) — VALIDATED */}
                <div className="mb-1">
                  <label className="form-label">Last Tyre Change (km) <span className="text-danger">*</span></label>
                  <input
                    type="number"
                    className="form-control"
                    value={vehicleData.lastTyreChangeKms || ''}
                    onChange={(e) => setKmWithClamp('lastTyreChangeKms', e.target.value)}
                    placeholder="Enter last tyre change"
                    min={0}
                    max={maxToNoOfKms()}
                    title="Must be ≤ Number of Kilometers"
                  />
                </div>
                {kmErrors.lastTyreChangeKms ? (
                  <small className="text-danger">{kmErrors.lastTyreChangeKms}</small>
                ) : null}

                {/* Last Tyre Change Date */}
                <div className="mb-3">
                  <label className="form-label">Last Tyre Change Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={normalizeDate(vehicleData.lastTyreChangeDate)}
                    max={todayStr}
                    onChange={(e) =>
                      setVehicleData({
                        ...vehicleData,
                        lastTyreChangeDate: clampToToday(e.target.value),
                      })
                    }
                  />
                </div>
              </div>
              {/* END RIGHT COLUMN */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepOne;
